import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useActions } from '../../queries/actions';
import { useAnalytics } from '../../queries/stats';
import { useProperties } from '../../queries/properties';
import { useProspects } from '../../queries/prospects';
import { useBookingsQuery } from '../../queries/scheduling';
import { useMeQuery } from '../../queries/me';
import { P } from '../../theme/tokens';
import { isVacant } from '../../utils/property';
import { STAGES, TERMINAL_STAGES } from '../../utils/prospects';
import { rangeParams } from '../Reports/data';
import { TYPE_LABEL } from '../Scheduling/constants';
import { DEFAULT_TZ, addDays, dateIn, mondayOf, startOfDayISO, timeIn, todayIn } from '../Scheduling/time';

const EMPTY = [];
const TREND_DAYS = 14;

// Booking type -> accent color for week-strip chips and the Coming-up list.
const TYPE_COLOR = { tour: P.info, appointment: P.purple, block: P.subtle };

const bookingEvent = (b, tz) => ({
  id: b._id,
  start: b.start,
  date: dateIn(b.start, tz),
  time: timeIn(b.start, tz),
  type: b.type,
  typeLabel: TYPE_LABEL[b.type] || 'Tour',
  color: TYPE_COLOR[b.type] || P.info,
  title: b.type === 'block' ? b.notes || 'Blocked time' : b.contact?.name || 'Unnamed contact',
  cancelled: b.status === 'cancelled'
});

export function useHomeData(can) {
  const { data: me } = useMeQuery();
  const tz = me?.organization?.scheduling?.timezone || DEFAULT_TZ;

  // Fixed windows (computed once per mount) so the query keys stay stable.
  const recapRange = useMemo(() => ({ from: dayjs().subtract(1, 'day').toISOString(), to: dayjs().toISOString() }), []);
  const trendRange = useMemo(() => rangeParams(TREND_DAYS), []);
  const week = useMemo(() => {
    const today = todayIn(tz);
    const start = mondayOf(today);
    return {
      today,
      days: Array.from({ length: 7 }, (_, i) => addDays(start, i)),
      range: { from: startOfDayISO(start, tz), to: startOfDayISO(addDays(start, 14), tz) }
    };
  }, [tz]);

  const canReports = can('view_reports');
  const recap = useAnalytics(recapRange, { enabled: canReports || can('view_calls') });
  const trend = useAnalytics(trendRange, { enabled: canReports });
  const actions = useActions('d');
  const props = useProperties();
  const prospects = useProspects();
  const bookings = useBookingsQuery(week.range);

  const kpis = recap.data?.kpis || {};
  const units = props.rows;
  const activeProspects = prospects.rows.filter((p) => !TERMINAL_STAGES.includes(p.pipelineStage));
  const events = (bookings.data || EMPTY).map((b) => bookingEvent(b, tz)).filter((e) => !e.cancelled);
  const needs = actions.rows.filter((x) => x.lane === 'needs' || x.lane === 'review');
  const todayEvents = events.filter((e) => e.date === week.today).sort((a, b) => (a.start < b.start ? -1 : 1));

  const stageCounts = activeProspects.reduce((o, p) => {
    o[p.pipelineStage] = (o[p.pipelineStage] || 0) + 1;
    return o;
  }, {});

  const now = dayjs();

  return {
    tz,
    canReports,
    recap: {
      calls: kpis.callsAnswered ?? 0,
      emergencies: kpis.emergencies ?? 0,
      tours: kpis.tourRequests ?? 0,
      workOrders: kpis.workOrders ?? 0,
      loading: recap.isLoading
    },
    trend: { series: trend.data?.series || EMPTY, loading: trend.isLoading },
    pointer: {
      needs: actions.rows.filter((x) => x.lane === 'needs').length,
      review: actions.rows.filter((x) => x.lane === 'review').length,
      top: needs.slice().sort((a, b) => rank(a) - rank(b))[0] || null,
      tz: actions.tz,
      isLoading: actions.isLoading
    },
    portfolio: {
      doors: units.length,
      listed: units.filter((u) => u.listed).length,
      vacant: units.filter(isVacant).length,
      loading: props.isLoading
    },
    today: { count: todayEvents.length, events: todayEvents, loading: bookings.isLoading },
    leasing: {
      count: activeProspects.length,
      stages: STAGES.filter((s) => stageCounts[s.key]).map((s) => ({ key: s.key, count: stageCounts[s.key] })),
      loading: prospects.isLoading
    },
    week: { days: week.days, today: week.today, events, loading: bookings.isLoading },
    coming: {
      rows: events
        .filter((e) => dayjs(e.start).isAfter(now))
        .sort((a, b) => (a.start < b.start ? -1 : 1))
        .slice(0, 6),
      loading: bookings.isLoading
    }
  };
}

const rank = (x) => (x.aging?.state === 'red' ? 0 : x.aging?.state === 'amber' ? 1 : 2);
